import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Loader2, Gauge, Sigma, Activity, Layers, Scale, ShieldCheck } from "lucide-react";

// Campos editables del motor de diagnóstico. Cada uno mapea a la ruta
// config[group][key] (o config[key] para valores planos como discrepancy_band).
const SECTIONS = [
  {
    id: "reference", icon: Sigma, color: "#2C49A0",
    title: "Referencia de estandarización (Z-score)",
    hint: "Distribución teórica para tipificar cuando la cohorte es pequeña, y n mínimo para usar la cohorte real.",
    fields: [
      { group: "reference", key: "mean", label: "Media teórica (μ)", step: 1, min: 0, max: 100 },
      { group: "reference", key: "sd", label: "Desviación estándar (σ)", step: 1, min: 1, max: 50 },
      { group: "reference", key: "min_cohort", label: "n mínimo de cohorte", step: 1, min: 2, max: 100 },
    ],
  },
  {
    id: "ice_zones", icon: Gauge, color: "#05A859",
    title: "Zonas del ICE global (0–100)",
    hint: "Umbral inferior de cada zona. ICE ≥ Verde → verde · ≥ Amarilla → amarilla · resto → roja.",
    fields: [
      { group: "ice_zones", key: "verde", label: "Umbral Zona Verde", step: 1, min: 0, max: 100 },
      { group: "ice_zones", key: "amarilla", label: "Umbral Zona Amarilla", step: 1, min: 0, max: 100 },
    ],
  },
  {
    id: "discrepancy", icon: Activity, color: "#A43790",
    title: "Discrepancia Emoción–Cognición",
    hint: "Banda de 'coherencia': si |Z_CEP − Z_CAD| ≤ banda, se considera percepción alineada con desempeño.",
    fields: [
      { flat: "discrepancy_band", label: "Banda de coherencia (Z)", step: 0.1, min: 0, max: 3 },
    ],
  },
  {
    id: "perfil", icon: Scale, color: "#F5B82E",
    title: "Clasificación de Perfil (I–IV)",
    hint: "Umbrales (escala 0–10) para considerar 'favorable' la estabilidad emocional y la organización cognitiva.",
    fields: [
      { group: "perfil", key: "emo_ok", label: "Umbral emocional favorable", step: 0.1, min: 0, max: 10 },
      { group: "perfil", key: "cog_ok", label: "Umbral cognitivo favorable", step: 0.1, min: 0, max: 10 },
    ],
  },
  {
    id: "andamiaje", icon: Layers, color: "#D63384",
    title: "Mapa de Andamiaje (por área CAD)",
    hint: "% de dominio por área: ≥ Consolidada → consolidada · ≥ Parcial → parcial · resto → fragmentada.",
    fields: [
      { group: "andamiaje", key: "consolidada", label: "Umbral Consolidada (%)", step: 1, min: 0, max: 100 },
      { group: "andamiaje", key: "parcial", label: "Umbral Parcial (%)", step: 1, min: 0, max: 100 },
    ],
  },
  {
    id: "cronbach", icon: ShieldCheck, color: "#6E7591",
    title: "Confiabilidad psicométrica",
    hint: "Coeficiente α de Cronbach mínimo para considerar internamente consistente el instrumento.",
    fields: [
      { flat: "cronbach_threshold", label: "α de Cronbach mínimo", step: 0.01, min: 0, max: 1 },
    ],
  },
];

export default function AdminConfigMotor() {
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/diagnostics/motor-config");
      setCfg(data.config);
      setDefaults(data.defaults);
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Solo el Super Administrador puede configurar el motor." : "Error al cargar la configuración.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setVal = (f, raw) => {
    const v = raw === "" ? "" : Number(raw);
    setCfg((c) => {
      if (f.flat) return { ...c, [f.flat]: v };
      return { ...c, [f.group]: { ...c[f.group], [f.key]: v } };
    });
  };
  const getVal = (f) => (f.flat ? cfg[f.flat] : cfg?.[f.group]?.[f.key]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/diagnostics/motor-config", cfg);
      setCfg(data.config);
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
      toast.success("Configuración del motor guardada ✨ Se aplicará en los próximos cálculos.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar.");
    } finally { setSaving(false); }
  };

  const resetAll = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/diagnostics/motor-config/reset");
      setCfg(data.config);
      toast.message("Umbrales restaurados a los valores por defecto (ya guardados).");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo restaurar.");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="jasq-card p-10 text-center text-sm text-[#6E7591]" data-testid="config-motor-loading">Cargando…</div>;
  if (!cfg) return <div className="jasq-card p-10 text-center text-sm text-[#A52258]" data-testid="config-motor-noaccess">Sin acceso a la configuración del motor.</div>;

  return (
    <div className="space-y-6" data-testid="admin-config-motor-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#A43790]">Super Administrador</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547] flex items-center gap-2">
            <Gauge className="text-[#A43790]" /> Configuración del Motor
          </h1>
          <p className="text-sm text-[#6E7591] mt-1">Ajusta los umbrales estadísticos del Diagnóstico Inteligente: Z-scores, zonas ICE, discrepancia, perfiles y andamiaje.</p>
          {meta.updated_at && (
            <p className="text-xs text-[#6E7591] mt-1">Última actualización: {new Date(meta.updated_at).toLocaleString("es-MX")}{meta.updated_by ? ` · por ${meta.updated_by}` : ""}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetAll} disabled={saving} data-testid="config-motor-reset"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-2xl border-2 border-[#E1E5F0] text-[#A52258] font-bold text-sm hover:bg-[#FDEBF4] transition-colors disabled:opacity-60">
            <RotateCcw size={16} /> Restaurar por defecto
          </button>
          <button onClick={save} disabled={saving} data-testid="config-motor-save"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#1F2547] text-white font-bold text-sm hover:bg-[#2C49A0] transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Guardar cambios
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {SECTIONS.map((sec) => {
          const Icon = sec.icon;
          return (
            <div key={sec.id} className="jasq-card p-5 space-y-4" data-testid={`config-motor-section-${sec.id}`}>
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-2xl inline-flex items-center justify-center shrink-0" style={{ background: `${sec.color}1A`, color: sec.color }}>
                  <Icon size={20} />
                </span>
                <div>
                  <h2 className="text-base font-extrabold text-[#1F2547]">{sec.title}</h2>
                  <p className="text-xs text-[#6E7591] mt-0.5 leading-relaxed">{sec.hint}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {sec.fields.map((f) => {
                  const tid = f.flat || `${f.group}-${f.key}`;
                  const def = f.flat ? defaults?.[f.flat] : defaults?.[f.group]?.[f.key];
                  return (
                    <div key={tid} className={sec.fields.length === 1 ? "col-span-2" : ""}>
                      <label className="text-[11px] uppercase tracking-wider font-extrabold text-[#6E7591]">{f.label}</label>
                      <input
                        type="number" step={f.step} min={f.min} max={f.max}
                        data-testid={`config-motor-input-${tid}`}
                        value={getVal(f) ?? ""}
                        onChange={(e) => setVal(f, e.target.value)}
                        className="mt-1 w-full px-3 py-2.5 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-bold text-sm tabular-nums"
                      />
                      {def !== undefined && <p className="text-[10px] text-[#A6ACC1] mt-1">Por defecto: {def}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
